import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/employee-redux";

import { uniq, flatten, values } from "lodash";
import { Loader } from "semantic-ui-react";

import * as ReferralReportExportsV4 from "@teamprevent/common/referralReport/components/ReferralReportV4";
import * as ReferralReportExportsV2_V3 from "@teamprevent/common/referralReport/components/ReferralReportV2_V3";
import * as OldReferralReportExports from "@teamprevent/common/referralReport/components/OldReferralReport";
import { QuestionnairesState } from "@teamprevent/common/questionnaires/model";
import { ConsultationDetails } from "@teamprevent/common/model";

import { ReferralReportDetails, ReferralReportVersion } from "@teamprevent/common/referralReport/models";

import { referralReportApi } from "../../referralReports/referralReportApi";
import { loadReferralReportByWriteTaskId, loadReferralReportByViewTaskId } from "../../referralReports/actions";
import { ApplicationState } from "../../applicationState";

import { Location, replace } from "redux-little-router";

interface ReportDetailProps {
    /**
     * @deprecated We should remove this from here when removing version as it'll no longer be required
     */
    oldVersionRedirectUrl?: string;
    /**
     * @deprecated We should remove this from here and add code to find out the Report Version from the reportDetails by looking up the question
     */
    version: number;
}

const ReportDetail: React.FunctionComponent<ReportDetailProps> = (props: ReportDetailProps) => {
    const dispatch = useDispatch();

    const [consultationAndReferralDetails, setConsultationAndReferralDetails] = React.useState<ConsultationDetails>(null);
    const [reportDetails, setReportDetails] = React.useState<ReferralReportDetails>(null);

    let QUESTIONNAIRE_KEY;
    switch (props.version) {
        case ReferralReportVersion.V1:
            QUESTIONNAIRE_KEY = OldReferralReportExports.QUESTIONNAIRE_KEY;
            break;
        case ReferralReportVersion.V2:
        case ReferralReportVersion.V3:
            QUESTIONNAIRE_KEY = ReferralReportExportsV2_V3.QUESTIONNAIRE_KEY;
            break;
        default:
            QUESTIONNAIRE_KEY = ReferralReportExportsV4.QUESTIONNAIRE_KEY;
    }

    let ReferralComponent;
    switch (props.version) {
        case ReferralReportVersion.V1:
            ReferralComponent = OldReferralReportExports.ReferralReport;
            break;
        case ReferralReportVersion.V2:
        case ReferralReportVersion.V3:
            ReferralComponent = ReferralReportExportsV2_V3.ReferralReport;
            break;
        default:
            ReferralComponent = ReferralReportExportsV4.ReferralReport;
    }

    const questions = useSelector((state: { questionnaires: QuestionnairesState }) =>
        uniq(flatten(state.questionnaires[QUESTIONNAIRE_KEY]?.sections.map(section => values(section.questions)))) || []);
    const eTag = useSelector((state: { questionnaires: QuestionnairesState }) => state.questionnaires[QUESTIONNAIRE_KEY]?.eTag);
    const isDefaultCustomerType =
        useSelector((state: { questionnaires: QuestionnairesState }) => state.questionnaires[QUESTIONNAIRE_KEY]?.isDefaultCustomerType);
    const customerType =
        useSelector((state: { questionnaires: QuestionnairesState }) => state.questionnaires[QUESTIONNAIRE_KEY]?.customerType);
    const report: OldReferralReportExports.ReferralReportModel |
                  ReferralReportExportsV2_V3.ReferralReportModel |
                  ReferralReportExportsV4.ReferralReportModel =
    {
        questions,
        eTag,
        isDefaultCustomerType,
        customerType
    };

    const customer = useSelector((state: ApplicationState) => state.customerDetails);
    const ohCaseId = useSelector((state: { router: Location }) => state.router.params?.ohCaseId);
    const writeReportTaskId = useSelector((state: { router: Location }) => state.router.params?.writeReportTaskId);
    const viewReportTaskId = useSelector((state: { router: Location }) => state.router.params?.viewReportTaskId);

    const loadQuestionnaire = () => {
        (async () => {
            const taskId = writeReportTaskId || viewReportTaskId;
            if (writeReportTaskId) {
                dispatch(loadReferralReportByWriteTaskId({ ohCaseId, writeReportTaskId }));
                const details = await referralReportApi.getConsultationDetailsByWriteTaskId(ohCaseId, taskId);
                setConsultationAndReferralDetails(details);
            } else if (viewReportTaskId) {
                dispatch(loadReferralReportByViewTaskId({ ohCaseId, viewReportTaskId }));
                const details = await referralReportApi.getConsultationDetailsByViewTaskId(ohCaseId, taskId);
                setConsultationAndReferralDetails(details);
            }
        })();
    };

    React.useEffect(() => {
        if (ohCaseId && (writeReportTaskId || viewReportTaskId)) {
            loadQuestionnaire();
        }

        (async () => {
            const details = await referralReportApi.getReportDetails(ohCaseId, writeReportTaskId, viewReportTaskId);
            setReportDetails(details);
        })();
    }, [ohCaseId, writeReportTaskId, viewReportTaskId]);

    if (!consultationAndReferralDetails || !reportDetails) {
        return <Loader active />;
    }

    return (
        <ReferralComponent
            useToyotaTemplates={customer.useToyotaTemplates}
            oldVersionRedirectUrl={props.oldVersionRedirectUrl}
            consultationAndReferralDetails={consultationAndReferralDetails}
            customerId={null}
            employeeRecord={null}
            loadConsultationDetails={() => null}
            loadConsultationDetailsPreview={() => null}
            loadEmployee={() => null}
            loadQuestionnaire={() => null}
            clearQuestionnaire={() => null}
            ohCase={null}
            ohCaseId={null}
            onCompletion={() => null}
            report={report}
            saveReport={() => null}
            readonly={true}
            taskId={null}
            submitInvalidQuestionnaire={() => null}
            updateAnswer={() => null}
            navigateToOldReport={(url: string) => dispatch(replace(url))}
            version={props.version}
            redirectData={{
                ohCaseId,
                viewReportTaskId
            }}
            reportDetails={reportDetails}
        />
    );
};

export { ReportDetail };
